import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { apiFetch } from '../lib/api';

export function InviteAcceptPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuthStore();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError(t('invite.invalidLink'));
      return;
    }

    if (!accessToken) {
      // Redirect to login, preserving the invite URL
      navigate(`/login?redirect=${encodeURIComponent(`/invite?token=${token}`)}`, { replace: true });
      return;
    }

    // Accept the invitation
    apiFetch<{ tree_id: string }>('/api/auth/invite/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }).then((res) => {
      if (res.ok) {
        setStatus('success');
        setTimeout(() => navigate(`/trees/${res.data.tree_id}`, { replace: true }), 1500);
      } else {
        setStatus('error');
        setError(res.error);
      }
    });
  }, [token, accessToken, navigate, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm text-center">
        {status === 'loading' && (
          <>
            <div className="w-10 h-10 border-2 border-brand-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-500">{t('invite.accepting')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-green-600">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-900">{t('invite.accepted')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('invite.redirecting')}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-red-600">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-red-700">{error}</p>
            <button
              onClick={() => navigate('/trees', { replace: true })}
              className="mt-4 text-sm text-brand-600 hover:text-brand-700 font-medium"
            >
              {t('invite.goToTrees')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
